import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as Sentry from '@sentry/angular';

import {
  faArrowCircleLeft,
  faCheckCircle,
  faCircleExclamation,
  faCircleInfo,
  faEraser,
  faSpinner,
  faUserGraduate,
  faUserPlus,
  faCircleXmark,
  faUsers,
  faCircleMinus,
  faCaretSquareRight,
  faCaretSquareDown,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { StudentsService } from 'src/app/core/services/students.service';
import { Student } from 'src/app/core/models/student.model';
import { SessionStorageService } from 'src/app/core/services/session-storage.service';
import { ManagementUser } from 'src/app/core/models/management-user.model';

import { coteacherIntroSteps } from './coteacher.intro-steps';
import { SchoolAdminTeacherFilterService } from 'src/app/shared/components/school-admin-teacher-filter/school-admin-teacher-filter.service';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

@Component({
    selector: 'wf-manage-coteachers',
    templateUrl: './coteacher.component.html',
    styleUrls: ['./coteacher.component.css'],
    standalone: false
})
export class AssignCoTeacherComponent implements OnInit {
  managementUser: ManagementUser | null = null ;
  isParentUser: boolean = false ;
  isDeleting: boolean = false ;
  userRole: string = '' ;
  messageTitle: string = '' ;
  message: string = '' ;
  messageStatus: string = '' ;
  teacherList: ManagementUser[] = [] ;
  /** Complete list of all loaded students (unfiltered) */
  studentList: Student[] = [] ;
  /** Currently displayed students */
  students: Student[] = [] ;
  selectedStudents: Set<number> = new Set<number>();
  studentsSelected: boolean = false ;
  enrollIcon = CATEGORY_ICONS['enroll'];
  studentIcon = faUserGraduate ;
  addTeacherIcon = faUserPlus ;
  loadingIcon = faSpinner ;
  successIcon = faCheckCircle ;
  errorIcon = faCircleExclamation ;
  closeIcon = faCircleXmark ;
  coTeacherIcon = faUsers ;
  removeCoTeacherIcon = faCircleMinus
  clearSelectionIcon = faEraser ;
  infoIcon = faCircleInfo ;
  backIcon = faArrowCircleLeft ;
  expandIcon = faCaretSquareRight ;
  collapseIcon = faCaretSquareDown ;
  helpIcon = faQuestionCircle ;
  showTeacherFilter: boolean = false;
  studentListTitle: string = 'My Students';
  teacherNamesById: Record<number, string> = {};

  // Co-Teacher props
  isAddingCoTeacher: boolean = false ;
  showAddCoTeacherDialog: boolean = false ;
  showAssignStudentsDialog: boolean = false ;
  selectedCoTeacherId?: number;
  myCoTeacherData: { [key: number]: (number | null)[] } = {};
  teachersWithAllAccess: any[] = [];
  filteredTeachers: any[] = [];

  selectedStudentForRevoke: number | null = null;
  studentCoTeacherMap: { [key: number]: (number | null)[] } = {};
  searchTerm: string = '';

  readonly tourSteps = coteacherIntroSteps;

  constructor(
    private route: ActivatedRoute,
    private studentService: StudentsService,
    private sessionService: SessionStorageService,
    private changeDetectorRef: ChangeDetectorRef,
    private teacherFilterService: SchoolAdminTeacherFilterService,
  ) { }

  ngOnInit(): void {
    this.managementUser = this.sessionService.userData() ;
    if (this.managementUser)
    {
      this.isParentUser = this.managementUser.isParentUser() ;
      this.userRole = this.managementUser.userRole ;
      this.showTeacherFilter = this.managementUser.isSchoolAdmin();
      this.teacherNamesById = this.teacherFilterService.getTeacherNamesById();
    }

    this.setStudentListTitle();
    this.studentList = this.route.snapshot.data['resolveData'].students;
    this.teacherList = this.route.snapshot.data['resolveData'].teachers;
    if (this.managementUser?.isSchoolAdmin()) {
      // Filter down students to the selected teacher
      this.students = this.teacherFilterService.filterStudentsBySelectedTeacher(this.studentList);
    } else {
      this.students = this.studentList;
    }

    this.queryAndProcessCoTeacherData();
  }

  studentToggled(studentId: number) {
    if (!this.selectedStudents.has(studentId)) {
      this.selectedStudents.add(studentId);
    } else {
      this.selectedStudents.delete(studentId);
    }

    this.studentsSelected = this.selectedStudents.size > 0 ;
  }

  clearStudentSelection() {
    this.selectedStudents.clear() ;
  }

  searchStudents() {
    if (!this.searchTerm) {
      return this.students;
    }

    return this.students.filter((student) => {
      return student.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        student.lastName.toLowerCase().includes(this.searchTerm.toLowerCase());
    })
  }

  clearMessage() {
    this.message = '';
    this.messageTitle = '';
    this.messageStatus = '';
  }

  openAddCoTeacherDialog() {
    // this.isAddingCoTeacher = true ; // should control spinner when service call is in progress
    this.showAddCoTeacherDialog = true ;
    this.selectedStudents.clear() ;
  }

  hideCoTeacherDialog() {
    // this.isAddingCoTeacher = false ;
    this.selectedStudents.clear() ;
    this.showAddCoTeacherDialog = false ;
  }

  openAssignStudentsDialog() {
    this.showAssignStudentsDialog = true ;
  }

  hideAssignStudentsDialog() {
    this.showAssignStudentsDialog = false ;
  }

  assignCoTeacherToSelectedStudents() {
    this.isAddingCoTeacher = true;
    const teacherId = this.managementUser?.userID;

    if (!teacherId || !this.selectedCoTeacherId || this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      return;
    }

    // block attempts to assign students if selected teacher already has full access
    if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(null)) {
      this.message = 'The selected teacher already has full co-teacher access.';
      this.messageTitle = 'Duplicate Assignment';
      this.messageStatus = 'error';
      this.isAddingCoTeacher = false;
      this.hideAssignStudentsDialog();
      return;
    }

    // Remove selected student ID's that are already assigned to the selected teacher
    for (const studentId of this.selectedStudents) {
      if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(studentId)) {
        this.selectedStudents.delete(studentId);
      } else if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(null)) {
        this.selectedStudents.delete(studentId);
      }

      // It's possible that no students are selected after removing duplicates
    if (this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      this.selectedStudents.clear() ;
      this.studentsSelected = false;
      this.hideAssignStudentsDialog();
      return;
    }
    }

    // It's possible that no students are selected after removing duplicates
    if (this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      this.selectedStudents.clear() ;
      this.studentsSelected = false;
      this.hideAssignStudentsDialog();
      return;
    }

    const primaryTeacherId = this.getPrimaryTeacherId();

    if (primaryTeacherId == null) {
      Sentry.captureException(new Error('No primary teacher ID passed when attempting to assign co-teacher access to all students'));
      this.messageTitle = 'Error Assigning Co-Teacher Access';
      this.messageStatus = 'error';
      this.message = 'An error occurred while assigning co-teacher access. Please try again later.';
      return;
    }

    // Add the teacher to the selected students
    this.studentService.assignCoTeacherAccess(primaryTeacherId, this.selectedCoTeacherId, Array.from(this.selectedStudents)).subscribe({
      next: (coteacherMap) => {
        // Update myCoTeacherData with newly assigned student IDs
        this.myCoTeacherData = coteacherMap ;

        // Update studentCoTeacherMap
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap();

        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'success';
        this.message = `Successfully assigned selected students to co-teacher`;

        this.isAddingCoTeacher = false;
        this.selectedStudents.clear();
        this.studentsSelected = false;
        this.hideAssignStudentsDialog();
      },
      error: (error) => {
        // Handle deadlock error
        if (error?.status === 500 && error?.error?.includes('Deadlock')) {
          this.messageTitle = 'Co-Teacher Assignment';
          this.messageStatus = 'error';
          this.message = 'There was a conflict assigning the co-teacher. Please try again.';
        } else {
          this.messageTitle = 'Error';
          this.messageStatus = 'error';
          this.message = 'Failed to assign co-teacher. Please try again later.';
        }
        this.isAddingCoTeacher = false;
      }
    });
  }

  assignCoTeacherAccessToAllStudents(coTeacherId?: number) {
    this.isAddingCoTeacher = true;

    if (!coTeacherId) {
      Sentry.captureException('No Teacher ID passed when attempting to assign co-teacher access to all Students') ;
      return ;
    }

    // We don't want to add a duplicate co-teacher, so check if the teacher is already assigned
    // If it is, then we check if it's an 'all students' permission. Which would be if the array
    // for the teacher contains a null value
    if (this.myCoTeacherData[coTeacherId]) {
      if (this.myCoTeacherData[coTeacherId].includes(null)) {
        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'error';
        this.message = 'The selected teacher already has access to all students.';
        this.isAddingCoTeacher = false;
        this.hideCoTeacherDialog();
        return ;
      }
    }

    const primaryTeacherId = this.getPrimaryTeacherId();

    if (primaryTeacherId == null) {
      Sentry.captureException(new Error('No primary teacher ID passed when attempting to assign co-teacher access to all students'));
      this.messageTitle = 'Error Assigning Co-Teacher Access';
      this.messageStatus = 'error';
      this.message = 'An error occurred while assigning co-teacher access. Please try again later.';
      return;
    }

    this.studentService.assignCoTeacherAccess(primaryTeacherId, coTeacherId, []).subscribe({
      next: (coteacherMap) => {
        // Update teachersWithAccess array
        this.myCoTeacherData = coteacherMap ;
        const selectedTeacher = this.filteredTeachers.find(t => t.userID === coTeacherId);
        if (selectedTeacher && !this.teachersWithAllAccess.includes(selectedTeacher)) {
          this.teachersWithAllAccess.push(selectedTeacher);
        }

        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;

        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'success';
        this.message = `Successfully assigned co-teacher`;
      }
    });

    this.isAddingCoTeacher = false;
    this.selectedStudents.clear();
    this.studentsSelected = false;

    this.hideCoTeacherDialog();

    this.studentService.getGrantedCoTeacherData().subscribe({
      next: (coteacherMap) => {
        this.myCoTeacherData = coteacherMap ;
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.markForCheck();
      }
    })
  }

  revokeCoTeacherAccess(coTeacherId: number) {
    this.isAddingCoTeacher = true;
    this.isDeleting = true ;

    const primaryTeacherId = this.getPrimaryTeacherId();

    if (primaryTeacherId == null) {
      Sentry.captureException(new Error('No primary teacher ID passed when attempting to revoke co-teacher access'));
      this.messageTitle = 'Error Revoking Co-Teacher Access';
      this.messageStatus = 'error';
      this.message = 'An error occurred while revoking co-teacher access. Please try again later.';
      return;
    }

    this.studentService.revokeCoTeacherAccess(primaryTeacherId, coTeacherId).subscribe({
      next: (coteacherMap) => {
        // Remove teacher from teachersWithAccess array
        this.myCoTeacherData = coteacherMap ;
        this.teachersWithAllAccess = this.teachersWithAllAccess.filter(
          teacher => teacher.userID !== coTeacherId
        );

        const revokedTeacher = this.filteredTeachers.find(t => t.userID === coTeacherId);
        this.messageTitle = 'Co-Teacher Removed';
        this.messageStatus = 'success';
        this.message = `Revoked report access for ${revokedTeacher?.fullName || 'teacher'}.`;

        this.isDeleting = false;
      },
      error: (error) => {
        Sentry.captureException(error) ;
        this.messageTitle = 'Error Removing Co-Teacher';
        this.messageStatus = 'error' ;
        this.message = 'An error occurred while removing co-teacher access. Please try again later.';

        this.isDeleting = false ;
      }
    });

    this.isAddingCoTeacher = false;
    this.selectedStudents.clear();
    this.studentsSelected = false;
  }

  setSelectedCoTeacher(teacherId: number) {
    this.selectedCoTeacherId = teacherId ;
  }

  getStudentCoTeachers(studentId: number): any[] {
    return this.studentCoTeacherMap[studentId] || [];
  }

  reprocessCoTeacherDataAndStudentMap(teachers: ManagementUser[]) {
    if (teachers.length === 0) {
      return;
    }

    this.filteredTeachers = teachers.map((teacher: ManagementUser) => {
      return {
        ...teacher,
        fullName: `${teacher.firstName} ${teacher.lastName}`
      }
    })
    .filter((teacher) => {
      // If school admin, filter out the teacher that is currently being viewed
      if (this.managementUser?.isSchoolAdmin()) {
        return teacher.userID !== this.teacherFilterService.getSelectedTeacherId();
      }

      // Otherwise, filter out the current user since this user is managing co-teachers for their own students
      return teacher.userID !== this.managementUser?.userID ;
    }) ;

    this.teachersWithAllAccess = this.filteredTeachers.filter((teacher) => {
      if (this.myCoTeacherData[teacher.userID] && this.myCoTeacherData[teacher.userID]?.includes(null)) {
        return true ;
      }
      return false ;
    }) ;

    this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;
  }

  generateStudentCoTeacherMap(): { [key: number]: (number | null)[] } {
    let studentCoTeacherMap: { [key: number]: (number | null)[] } = {} ;

    // From the co-teacher map, generate a map of student -> teachers
    Object.keys(this.myCoTeacherData).forEach((teacherIdStr: string) => {
      const teacherId = parseInt(teacherIdStr) ;
      this.myCoTeacherData[teacherId].forEach((studentId) => {
        if (studentId === null) return ;

        studentCoTeacherMap[studentId] = this.filteredTeachers.filter(teacher => {
          return this.myCoTeacherData[teacher.userID]?.includes(studentId);
        });
      });
    }) ;

    return studentCoTeacherMap ;
  }

  hasCoTeachers(studentId: number): boolean {
    return (this.studentCoTeacherMap[studentId] || []).length > 0;
  }

  toggleStudentCoTeacherList(studentId: number) {
    this.selectedStudentForRevoke = this.selectedStudentForRevoke === studentId ? null : studentId;
  }

  revokeCoTeacherAccessForStudent(coTeacherId: number, studentId: number) {
    this.isAddingCoTeacher = true;

    const primaryTeacherId = this.getPrimaryTeacherId();

    if (primaryTeacherId == null) {
      Sentry.captureException(new Error('No primary teacher ID passed when attempting to revoke co-teacher access'));
      this.messageTitle = 'Error Revoking Co-Teacher Access';
      this.messageStatus = 'error';
      this.message = 'An error occurred while revoking co-teacher access. Please try again later.';
      return;
    }

    this.studentService.revokeCoTeacherAccess(primaryTeacherId, coTeacherId, studentId).subscribe({
      next: (coteacherMap) => {
        // Update the coTeacherData to remove this student
        this.myCoTeacherData = coteacherMap ;
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;

        const revokedTeacher = this.filteredTeachers.find(t => t.userID === coTeacherId);
        const student = this.students.find(s => s.userID === studentId);

        if (!this.hasCoTeachers(studentId)) {
          this.selectedStudentForRevoke = null ;
        }

        this.messageTitle = 'Co-Teacher Access Updated';
        this.messageStatus = 'success';
        this.message = `Revoked ${revokedTeacher?.fullName || 'teacher'}'s access to ${student?.firstName}'s reports.`;

        this.isAddingCoTeacher = false;
      },
      error: (error) => {
        this.messageTitle = 'Error';
        this.messageStatus = 'error';
        this.message = 'Failed to revoke co-teacher access. Please try again later.';
        this.isAddingCoTeacher = false;
      }
    });
  }

  onTeacherFilterChange() {
    this.setStudentListTitle();
    this.students = this.teacherFilterService.filterStudentsBySelectedTeacher(this.studentList);
    this.queryAndProcessCoTeacherData();
  }

  queryAndProcessCoTeacherData() {
    const teacherID = this.managementUser?.isSchoolAdmin() ? this.teacherFilterService.getSelectedTeacherId() : this.managementUser?.userID;
    if (teacherID == null || teacherID == 0) {
      // We should not be querying for co-teacher data for "all" teachers at once
      return;
    }

    this.studentService.getGrantedCoTeacherData(teacherID)
      .subscribe((coTeacherData) => {
        this.myCoTeacherData = coTeacherData;
        this.reprocessCoTeacherDataAndStudentMap(this.teacherList);
      });
  }

  getPrimaryTeacherId(): number | null {
    return this.managementUser?.isSchoolAdmin()
      ? this.teacherFilterService.getSelectedTeacherId()
      : this.managementUser?.userID ?? null;
  }

  setStudentListTitle() {
    const schoolAdminFilteredTeacherId = this.teacherFilterService.getSelectedTeacherId();
    if (this.managementUser != null && this.managementUser.isSchoolAdmin()) {
      this.studentListTitle = schoolAdminFilteredTeacherId ? `${this.teacherNamesById[schoolAdminFilteredTeacherId]}'s Students` : 'Students';
    } else {
      this.studentListTitle = 'My Students';
    }
  }
}
